import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css'; // Import CSS
import { useTranslation } from 'react-i18next';
import { cropImage, getTaskStatus } from '../../services/chatApi';
import { Download, Upload, Crop, RefreshCw, Loader2, Square, Maximize } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};



// Helper to center crop programmatically
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

const CropImage = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // React-Image-Crop state
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const imgRef = useRef(null);
    const pollInterval = useRef(null);

     
    const resetParams = useCallback(() => {
        setCrop(undefined);
        setCompletedCrop(undefined);
        setError(null);
    }, []);

    useImageTransfer(setFile, setPreviewUrl, setProcessedImage, resetParams);
    useEffect(() => {
        return () => clearInterval(pollInterval.current);
    }, []);
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setProcessedImage(null);
            resetParams(); // Reset crop
        }
    };

    // Triggered when image loads in the cropper
    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        // Default to full selection minus a bit of padding
        const initialCrop = centerAspectCrop(width, height, width / height);
        setCrop(initialCrop);
        setCompletedCrop(initialCrop);
    };

    const setToSquare = () => {
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const newCrop = centerAspectCrop(width, height, 1);
            setCrop(newCrop);
            setCompletedCrop(newCrop);
        }
    };

    const resetCrop = () => {
        if (imgRef.current) {
            const { width, height } = imgRef.current;
            const newCrop = centerAspectCrop(width, height, width / height);
            setCrop(newCrop);
            setCompletedCrop(newCrop);
        }
    };

    const handleProcess = async () => {
        if (!file || !completedCrop || !imgRef.current) return;
        
        setIsLoading(true);
        setError(null);
        setProcessedImage(null);
        
        try {
            // We need to map the CSS coordinates (pixels on screen) to Actual Image coordinates
            const image = imgRef.current;
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            const params = {
                x: completedCrop.x * scaleX,
                y: completedCrop.y * scaleY,
                width: completedCrop.width * scaleX,
                height: completedCrop.height * scaleY
            };

            const startResponse = await cropImage(file, params);
            const taskId = startResponse.task_id;

            if (!taskId) throw new Error("No Task ID returned");

            // Poll for Completion
            pollInterval.current = setInterval(async () => {
                try {
                    const statusRes = await getTaskStatus(taskId);
                    const status = statusRes.status || statusRes.state;

                    if (status === 'SUCCESS' || status === 'completed') {
                        clearInterval(pollInterval.current);
                        
                        const result = statusRes.result || statusRes.info;
                        
			if (result.new_credits !== undefined) {
                            setCredits(result.new_credits);
                        }

                        if (result.download_url) {
                            setProcessedImage(result.download_url);
                        } else {
                            setError("Invalid response format from server.");
                        }
                        setIsLoading(false);

                    } else if (status === 'FAILURE' || status === 'failed') {
                        clearInterval(pollInterval.current);
                        setIsLoading(false);
                        setError(t('cropImage.errors.generic') || "Crop failed");
                    }
                } catch (pollErr) {
                    clearInterval(pollInterval.current);
                    setIsLoading(false);
                    setError("Network error while checking status.");
                }
            }, 2000);	     

        } catch (err) {
            setError(t('cropImage.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!processedImage) return;
        try {
            const response = await fetch(processedImage);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = "cropped_image.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            window.open(processedImage, '_blank');
        }
    };
    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setProcessedImage(null);
        setCrop(undefined);
        setCompletedCrop(undefined);
        clearInterval(pollInterval.current);
    };

    

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('cropImage.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-2xl">
                    {t('cropImage.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="crop-upload" />
                        <label htmlFor="crop-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Upload size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('cropImage.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('cropImage.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Cropper Interface */}
                        <div className="lg:col-span-8">
                             {/* Editor */}
                             <div className="bg-slate-100 p-4 rounded-lg border border-slate-200 flex justify-center">
                                {!processedImage ? (
                                    <ReactCrop crop={crop} 
				     onChange={(c) => setCrop(c)}
				     onComplete={(c) => setCompletedCrop(c)}
				     >
                                        <img 
                                            ref={imgRef}
                                            src={previewUrl} 
                                            alt="Crop editor" 
                                            onLoad={onImageLoad}
                                            className="max-h-[600px] w-auto" // Limit height to keep UI clean
                                        />
                                    </ReactCrop>
                                ) : (
                                    <img src={processedImage} alt="Result" className="max-h-[600px] w-auto" />
                                )}
                             </div>

                             {/* Navigation for processed image */}
                             {processedImage && (
                                <div className="mt-4">
                                     <ToolNavigator currentImage={processedImage} />
                                </div>
                             )}
                             
                             {error && <div className="mt-4 text-red-600 bg-red-50 p-3 rounded">{error}</div>}
                        </div>

                        {/* Right Column: Controls */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                                <h4 className="font-semibold mb-4 flex items-center gap-2 text-brand-orange">
                                    <Crop size={20} /> {t('cropImage.controls.title')}
                                </h4>
                                
                                {!processedImage ? (
                                    <div className="space-y-3">
                                        <button onClick={resetCrop} className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded transition">
                                            <Maximize size={16} /> {t('cropImage.buttons.resetFull')}
                                        </button>
                                        <button onClick={setToSquare} className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded transition">
                                            <Square size={16} /> {t('cropImage.buttons.centerSquare')}
                                        </button>
                                        
                                        <div className="pt-4 border-t border-slate-100 mt-4">
                                            <button 
                                                onClick={handleProcess} 
                                                disabled={isLoading || (completedCrop?.width === 0 && completedCrop?.height === 0)} 
                                                className="w-full bg-brand-orange text-white py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center gap-2 font-semibold disabled:opacity-70"
                                            >
                                                {isLoading ? <Loader2 className="animate-spin" /> : <Crop />} 
                                                {t('cropImage.buttons.crop')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <button onClick={handleDownload} className="w-full bg-green-600 text-white py-3 rounded-md hover:bg-green-700 transition flex items-center justify-center gap-2 font-semibold">
                                            <Download size={18} /> {t('cropImage.buttons.download')}
                                        </button>
                                        <button onClick={() => setProcessedImage(null)} className="w-full bg-slate-200 text-slate-700 py-3 rounded-md hover:bg-slate-300 transition flex items-center justify-center gap-2">
                                            <Crop size={18} /> {t('cropImage.buttons.edit')}
                                        </button>
                                        <button onClick={resetTool} className="w-full bg-slate-100 text-slate-700 py-3 rounded-md hover:bg-slate-200 transition flex items-center justify-center gap-2">
                                            <RefreshCw size={18} /> {t('cropImage.buttons.newImage')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {!processedImage && (
                                <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm text-blue-800">
                                    💡 {t('cropImage.tip')}
                                </div>
                            )}
                        </div>

                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default CropImage;
